import PropTypes from 'prop-types'
import {
  Box,
  Grid,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import useTableStyles from '@styles/table/table'

interface ChartSkeletonProps {
  width?: string | number
  height?: string | number
}

export const ChartSkeleton: React.FC<ChartSkeletonProps> = ({
  width = '100%',
  height = '100%'
}) => {
  return (
    <Box
      sx={{
        height,
        width,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch'
      }}
    >
      <Skeleton width={'100%'} height={'100%'} variant="rectangular">
        <div />
      </Skeleton>
      <Box
        component="div"
        sx={{
          width: '70%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          mt: (t: Theme) => t.spacing(2),
          mx: 'auto'
        }}
      >
        <Skeleton />
      </Box>
    </Box>
  )
}

export const SelectSkeleton: React.FC = () => {
  return (
    <Box
      component="div"
      sx={{ width: '100%', height: (t: Theme) => t.spacing(6) }}
    >
      <Skeleton
        height={'100%'}
        variant="rectangular"
        sx={{ borderRadius: (t: Theme) => t.spacing(1) }}
      />
    </Box>
  )
}

export const SelectMultilineSkeleton: React.FC = () => {
  return (
    <Box
      component="div"
      sx={{ width: '100%', height: (t: Theme) => t.spacing(12) }}
    >
      <Skeleton
        height={'100%'}
        variant="rectangular"
        sx={{ borderRadius: (t: Theme) => t.spacing(1) }}
      />
    </Box>
  )
}

interface TextSkeletonProps {
  linesCount?: number
  spacing?: number
  sx?: object
}

export const TextSkeleton: React.FC<TextSkeletonProps> = ({
  linesCount = 1,
  spacing = 0,
  sx = { width: '100%', mb: (t: Theme) => t.spacing(3) }
}) => {
  return (
    <Stack spacing={spacing} sx={sx} component="div">
      {[...new Array(linesCount)].map((e, i) => (
        <Skeleton key={i} />
      ))}
    </Stack>
  )
}

interface ChipSkeletonProps {
  width?: (t: Theme) => number | string
}

export const ChipSkeleton: React.FC<ChipSkeletonProps> = ({
  width = (t: Theme) => t.spacing(12)
}) => {
  return (
    <Box
      component="div"
      sx={{ width: width, height: (t: Theme) => t.spacing(4) }}
    >
      <Skeleton
        width={'100%'}
        height={'100%'}
        variant="rectangular"
        sx={{ borderRadius: (t: Theme) => t.spacing(3) }}
      />
    </Box>
  )
}

interface ButtonSkeletonProps {
  count?: number
  spacing?: number
  borderRadius?: (t: Theme) => number | string
}

export const ButtonSkeleton: React.FC<ButtonSkeletonProps> = ({
  count = 1,
  spacing = 2,
  borderRadius = (t: Theme) => t.spacing(1)
}) => {
  return (
    <Stack direction="row" spacing={spacing} sx={{ width: 'fit-content' }}>
      {[...new Array(count)].map((e, i) => (
        <Box
          key={i}
          component="div"
          sx={{
            width: (t: Theme) => t.spacing(12),
            height: (t: Theme) => t.spacing(5)
          }}
        >
          <Skeleton
            height={'100%'}
            variant="rectangular"
            sx={{ borderRadius }}
          />
        </Box>
      ))}
    </Stack>
  )
}

interface CheckboxSkeletonProps {
  linesCount?: number
  sx?: object
  spacing?: number
}

export const CheckboxSkeleton: React.FC<CheckboxSkeletonProps> = ({
  linesCount = 1,
  sx = { width: '100%' },
  spacing = 2
}) => {
  return (
    <Stack sx={sx} spacing={2}>
      {[...new Array(linesCount)].map((e, i) => (
        <Stack key={i} direction="row" spacing={spacing} width="100%">
          <span data-testid="span-50b74f16-acf2-4b0c-a744-6c92d066d794">
            <Skeleton
              variant="rectangular"
              sx={{ width: '20px', height: '20px' }}
            />
          </span>
          <Skeleton key={i} width="100%" />
        </Stack>
      ))}
    </Stack>
  )
}

interface TableCellSkeletonProps {
  linesCount?: number
  borderCell?: boolean
}

export const TableCellSkeleton: React.FC<TableCellSkeletonProps> = ({
  linesCount = 1,
  borderCell = false
}) => {
  const classesTable = useTableStyles()
  return (
    <TableCell className={borderCell ? classesTable.borderCell : ''}>
      <Box
        component="div"
        sx={{
          width: '70%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch'
        }}
      >
        {[...new Array(linesCount)].map((e, i) => (
          <Skeleton key={i} />
        ))}
      </Box>
    </TableCell>
  )
}

interface TableRowSkeletonProps {
  rowsCount?: number
  cellsCount?: number
}

export const TableRowSkeleton: React.FC<TableRowSkeletonProps> = ({
  rowsCount = 1,
  cellsCount = 3
}) => {
  const classesTable = useTableStyles()
  return [...new Array(rowsCount)].map((e, i) => (
    <TableRow key={i} className={classesTable.row}>
      {[...new Array(cellsCount)].map((e, i) => (
        <TableCellSkeleton key={i + rowsCount} />
      ))}
    </TableRow>
  ))
}

interface TableSkeletonProps {
  rowsCount?: number
  cellsCount?: number
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rowsCount = 1,
  cellsCount = 3
}) => {
  const classesTable = useTableStyles()
  return (
    <TableContainer className={classesTable.container}>
      <Table>
        <TableHead>
          <TableRow hover={false}>
            {[...new Array(cellsCount)].map((e, i) => (
              <TableCellSkeleton key={i + rowsCount} />
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[...new Array(rowsCount)].map((e, i) => (
            <TableRow key={i} className={classesTable.row}>
              {[...new Array(cellsCount)].map((e, i) => (
                <TableCellSkeleton key={i + rowsCount} />
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export const PDFSkeleton: React.FC = () => {
  return (
    <Stack
      justifyContent="space-between"
      sx={{ height: '50vh' }}
      alignItems="center"
      mt={15}
    >
      <Box
        sx={{
          width: '30%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          mx: 'auto'
        }}
      >
        <Skeleton />
      </Box>
      <Box
        component="div"
        sx={{
          width: '60%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          mx: 'auto'
        }}
      >
        {[...Array(2)].map((el, index) => (
          <Skeleton sx={{ marginTop: '0.5rem' }} key={index} />
        ))}
      </Box>
      <Box
        component="div"
        sx={{
          width: '60%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          mx: 'auto'
        }}
      >
        {[...Array(4)].map((el, index) => (
          <Skeleton sx={{ marginTop: '0.5rem' }} key={index} />
        ))}
      </Box>
      <Box
        component="div"
        sx={{
          width: '60%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          mx: 'auto'
        }}
      >
        <Skeleton sx={{ marginTop: '0.8rem' }} />
        <Skeleton sx={{ marginTop: '0.8rem' }} />
      </Box>
    </Stack>
  )
}

interface MetricSkeletonProps {
  withProgressBar?: boolean
}

export const MetricSkeleton: React.FC<MetricSkeletonProps> = ({
  withProgressBar = false
}) => {
  return (
    <Grid
      container
      padding={3}
      sx={{
        height: '100%',
        backgroundColor: theme => theme.palette.primary.contrastText,
        borderRadius: theme => theme.spacing(3)
      }}
    >
      <Stack spacing={1} width="100%">
        <Typography
          variant="h6"
          sx={{ width: '60%' }}
          data-testid="Typography-041a137b-034a-4564-8143-7833fcbd121d"
        >
          <Skeleton />
        </Typography>
        {withProgressBar && (
          <Typography
            variant="caption"
            sx={{ width: '100%' }}
            data-testid="Typography-b172fdfc-b934-4ac3-9d77-96782d2059b5"
          >
            <Skeleton />
          </Typography>
        )}

        <Typography
          variant="caption"
          sx={{ width: '80%' }}
          data-testid="Typography-d431889b-e551-442b-bfe7-28f407f0249e"
        >
          <Skeleton />
        </Typography>
      </Stack>
    </Grid>
  )
}

interface GroupMetricsSkeletonProps {
  number: number
}

export const GroupMetricsSkeleton: React.FC<GroupMetricsSkeletonProps> = ({
  number
}) => {
  return (
    <>
      {[...Array(number)].map((_, index) => (
        <Grid key={index} item xs={12} md={12 / number}>
          <MetricSkeleton />
        </Grid>
      ))}
    </>
  )
}

interface ProjectSummaryCardSkeletonProps {
  number?: number
}

export const ProjectSummaryCardSkeleton: React.FC<
  ProjectSummaryCardSkeletonProps
> = ({ number = 1 }) => {
  return (
    <>
      {[...Array(number)].map((_, index) => (
        <Stack direction="row" spacing={3} p={1} key={index}>
          <Skeleton
            variant="rectangular"
            sx={{ width: 90, height: 90, borderRadius: '10px' }}
          />

          <Stack spacing={1}>
            <Skeleton variant="rectangular" sx={{ width: 200, height: 15 }} />
            <Skeleton variant="rectangular" sx={{ width: 200, height: 10 }} />

            <Skeleton
              variant="rectangular"
              sx={{ width: 400, height: 15, mt: '15px !important' }}
            />
            <Skeleton variant="rectangular" sx={{ width: 400, height: 20 }} />
          </Stack>
        </Stack>
      ))}
    </>
  )
}
